/**
 * Structured Logger Module
 *
 * Centralized logging utility that provides:
 * - Log level filtering based on environment
 * - JSON format in production for log aggregation
 * - Human-readable format with emojis in development
 *
 * Extracted from api-utils.ts as part of module decomposition.
 */

export type LogLevel = "debug" | "info" | "warn" | "error";

interface LogContext {
  [key: string]: unknown;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const currentLogLevel: LogLevel =
  (process.env.LOG_LEVEL as LogLevel) ||
  (process.env.NODE_ENV === "production" ? "info" : "debug");

/**
 * Structured logger that replaces console.log throughout the codebase.
 * Outputs JSON in production for log aggregation, human-readable in development.
 */
export const logger = {
  log(level: LogLevel, message: string, context?: LogContext): void {
    if (LOG_LEVELS[level] < LOG_LEVELS[currentLogLevel]) {
      return;
    }

    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level,
      message,
      ...context,
    };

    if (process.env.NODE_ENV === "production") {
      // JSON format for production log aggregation
      console[level === "debug" ? "log" : level](JSON.stringify(logEntry));
    } else {
      // Human-readable format for development
      const prefix = {
        debug: "🔍",
        info: "ℹ️",
        warn: "⚠️",
        error: "❌",
      }[level];

      const contextStr = context
        ? ` ${JSON.stringify(context, null, 2)}`
        : "";
      console[level === "debug" ? "log" : level](
        `${prefix} [${timestamp}] ${message}${contextStr}`
      );
    }
  },

  debug(message: string, context?: LogContext): void {
    this.log("debug", message, context);
  },

  info(message: string, context?: LogContext): void {
    this.log("info", message, context);
  },

  warn(message: string, context?: LogContext): void {
    this.log("warn", message, context);
  },

  error(message: string, context?: LogContext): void {
    this.log("error", message, context);
  },
};
